import { useRef, useState } from 'react';
import { navigate } from '../../app/navigation';
import { useT, type MessageKey } from '../../i18n/i18n';
import { importInspectionFile, type ImportProblem } from '../../state/inspectionTransfer';
import { Button, showToast } from '../../ui/components';

const LONG_TOAST_MS = 7000;

const PROBLEM_KEYS: Record<ImportProblem, MessageKey> = {
  notAFile: 'importFile.problem.notAFile',
  damaged: 'importFile.problem.damaged',
  newerFormat: 'importFile.problem.newerFormat',
  otherAccount: 'importFile.problem.otherAccount',
  alreadyHere: 'importFile.problem.alreadyHere',
  finished: 'importFile.problem.finished',
  notSignedIn: 'importFile.problem.notSignedIn',
  storageFull: 'importFile.problem.storageFull',
  failed: 'importFile.problem.failed'
};

// 「从文件导入一单」 (Owner 2026-10-20): picks the file another device exported (AirDrop, Files, a drive, a cable)
// and opens the inspection once it is in. Every refusal is said in words — never a silent nothing.
export function ImportInspectionButton(props: { variant?: 'text' | 'outline' | 'secondary'; className?: string }) {
  const { t, tp } = useT();
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  async function take(file: File) {
    setBusy(true);
    try {
      const result = await importInspectionFile(file);
      if (result.kind === 'imported') {
        showToast(tp('importFile.done', result.photos, { address: result.address || t('inspection.noAddress') }), 'success', LONG_TOAST_MS);
        navigate({ screen: 'inspection', inspectionId: result.inspectionId });
      } else showToast(t(PROBLEM_KEYS[result.reason], { detail: result.detail }), 'info', LONG_TOAST_MS);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button variant={props.variant ?? 'text'} icon="upload_file" onClick={() => input.current?.click()} disabled={busy} testId="import-inspection" className={props.className}>
        {busy ? t('importFile.working') : t('inspections.importFile')}
      </Button>
      <input
        ref={input}
        type="file"
        accept=".zip,application/zip"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0] ?? null;
          // The same file may be picked again after a refusal.
          event.target.value = '';
          if (file) void take(file);
        }}
        data-testid="import-inspection-input"
      />
    </>
  );
}
